import { useState } from 'react'
import { formatCents } from '@core/money'
import { parsePhrase } from '@core/phrase'
import { Card } from '@design/components'

/**
 * "Metro 1289,70 vrijdag", filled into the form below.
 *
 * spec.md §354 puts the typed sentence first and the form "one tap below", and
 * is emphatic that a parser which fails must fall gracefully into the form
 * rather than scold you. So this never saves anything and never blocks
 * anything: it fills fields in, and the form stays exactly as it was.
 *
 * **It shows what it understood before you press it.** A parser that quietly
 * guesses a date is worse than no parser — the whole cost of being wrong lands
 * on someone who has already stopped paying attention. What it read is on
 * screen, in words, while there is still nothing at stake.
 */
export function PhraseLine({
  onFill,
}: {
  onFill: (parsed: { description: string; amount: string; dueDate: string }) => void
}) {
  const [text, setText] = useState('')
  const parsed = text.trim() ? parsePhrase(text) : null
  const understood = parsed && (parsed.amountCents !== null || parsed.date !== null)

  return (
    <Card className="mb-4">
      <label htmlFor="phrase" className="block text-sm text-muted mb-1.5">
        In één zin
      </label>
      <input
        id="phrase"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && understood) {
            e.preventDefault()
            fill()
          }
        }}
        placeholder="Metro 1289,70 vrijdag"
        className="w-full min-h-[3rem] px-3 rounded-xl border border-line bg-transparent text-base"
      />

      {parsed && !understood && (
        <p className="text-sm text-muted mt-2">
          Hier haal ik geen bedrag of datum uit. Vul het hieronder in.
        </p>
      )}

      {understood && (
        <>
          <p className="text-sm mt-2">
            {[
              parsed.description || 'zonder omschrijving',
              parsed.amountCents !== null ? formatCents(parsed.amountCents) : null,
              parsed.date ? longDate(parsed.date) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <button
            onClick={fill}
            className="mt-2 min-h-[2.75rem] px-4 rounded-xl border border-ink bg-ink/5 text-sm font-medium"
          >
            Invullen
          </button>
        </>
      )}
    </Card>
  )

  function fill() {
    if (!parsed) return
    onFill({
      description: parsed.description,
      // The form's own field, in the format a person types — so it goes back
      // through the same validator as everything typed by hand.
      amount: parsed.amountCents !== null ? centsToTyped(parsed.amountCents) : '',
      dueDate: parsed.date ?? '',
    })
    setText('')
  }
}

function centsToTyped(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',')
}

function longDate(iso: string): string {
  return new Intl.DateTimeFormat('nl-BE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${iso}T00:00:00`))
}
